import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'
import {
  Alert,
  AuthCard,
  Button,
  Input,
  OtpInput,
  PasswordInput,
  useAuthSession,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'

/** Connexion admin en deux temps : identifiants, puis code TOTP si la MFA est active. */
export function LoginPage() {
  const { t } = useTranslation()
  const session = useAuthSession()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [challenge, setChallenge] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [recoveryMode, setRecoveryMode] = useState(false)

  const login = useMutation({
    mutationFn: () => session.login(email, password),
    onSuccess: (response) => {
      if (response.status === 'mfa_required') setChallenge(response.challenge)
      else navigate(from, { replace: true })
    },
  })

  const mfa = useMutation({
    mutationFn: (value: string) => session.completeMfa(challenge!, value),
    onSuccess: () => navigate(from, { replace: true }),
    onError: () => setCode(''),
  })

  function submitCredentials(event: FormEvent) {
    event.preventDefault()
    login.mutate()
  }

  if (challenge) {
    return (
      <AuthCard
        title={t('auth.mfa.title')}
        description={t('auth.mfa.description')}
        footer={
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setChallenge(null)
              setPassword('')
              mfa.reset()
            }}
          >
            {t('auth.mfa.back')}
          </button>
        }
      >
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            mfa.mutate(code)
          }}
        >
          {mfa.isError && <Alert tone="danger">{errorMessage(mfa.error)}</Alert>}
          {recoveryMode ? (
            <Input
              label={t('auth.mfa.recovery')}
              mono
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              hint={t('auth.mfa.recoveryHint')}
              data-autofocus
            />
          ) : (
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={(value) => mfa.mutate(value)}
              disabled={mfa.isPending}
              invalid={mfa.isError}
            />
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={mfa.isPending}
            disabled={code.replace(/\W/g, '').length < 6}
          >
            {t('auth.mfa.submit')}
          </Button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setRecoveryMode((v) => !v)
              setCode('')
            }}
          >
            {recoveryMode ? t('auth.mfa.useTotp') : t('auth.mfa.useRecovery')}
          </button>
        </form>
      </AuthCard>
    )
  }

  return (
    <AuthCard
      title={t('auth.login.title')}
      description={t('auth.login.description')}
      footer={t('auth.login.noSignup')}
    >
      <form className="stack" onSubmit={submitCredentials} noValidate>
        {login.isError && <Alert tone="danger">{errorMessage(login.error)}</Alert>}
        <Input
          label={t('auth.login.email')}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordInput
          label={t('auth.login.password')}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div
          className="row"
          style={{ justifyContent: 'flex-end', marginBlockStart: 'calc(-1 * var(--space-2))' }}
        >
          <Link to="/mot-de-passe-oublie" style={{ fontSize: 'var(--text-sm)' }}>
            {t('auth.login.forgot')}
          </Link>
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={login.isPending}
          disabled={!email || !password}
        >
          {t('auth.login.submit')}
        </Button>
      </form>
    </AuthCard>
  )
}
